import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import API from "../../services/api";
import { getReportLastDay, getReportMonthTimestamps } from "./reportDateUtils";

const TEAM = "metro";

const MONTH_SHORT_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function getPreviousMonthLabel(year, month) {
  // month is 1-12
  const shortName = MONTH_SHORT_NAMES[month - 1];
  const yearSuffix = String(year).slice(-2);
  return `${shortName}_${yearSuffix}`;
}

const COLORS = {
  titleBg: "#F39C12",
  titleText: "#000",
  headerBg: "#21D04B",
  headerText: "#000",
  totalRowBg: "#FFFF00",
  prevMonthLabelBg: "#1F1F66",
  prevMonthLabelText: "#FFFFFF",
  prevMonthTotalBg: "#FDE3C2",
};

function PreviousMonthRow({ label, data, displayedTeamMembers }) {
  const calculateSpentByIds = (salesIds) => {
    return data.reduce((sum, entry) => {
      if (salesIds.includes(entry.sales_id)) {
        return sum + (parseFloat(entry.spent) || 0);
      }
      return sum;
    }, 0);
  };

  const calculateTotalSpent = () => {
    return Math.round(data.reduce((sum, entry) => sum + (parseFloat(entry.spent) || 0), 0));
  };

  return (
    <tr>
      <td
        style={{
          background: COLORS.prevMonthLabelBg,
          color: COLORS.prevMonthLabelText,
          textAlign: "center",
        }}
      >
        <strong style={{ color: COLORS.prevMonthLabelText }}>{label}</strong>
      </td>
      {displayedTeamMembers?.map((item, index) => {
        const ids = item?.isClubbed ? item.sales_person_ids : [item.sales_person_id];
        const total = calculateSpentByIds(ids);
        return (
          <td key={`${item?.name}-${label}-${index}`} title={total}>
            {Math.round(total)}
          </td>
        );
      })}
      <td
        title={calculateTotalSpent()}
        style={{ background: COLORS.prevMonthTotalBg, fontWeight: "bold" }}
      >
        {calculateTotalSpent()}
      </td>
    </tr>
  );
}

function MetroCategoryReport(props) {
  const { year, month, newReports } = props;
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [teamData, setTeamData] = useState([]);
  const [previousMonths, setPreviousMonths] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [currentDay, setCurrentDay] = useState(getReportLastDay(year, month - 1));

  const { startTimestamp, endTimestamp } = getReportMonthTimestamps(year, month);

  const getTimestamp = (times) => {
    const startDate = new Date(year, month - 1, 1);
    const timestampFirstDayOfMonth = Math.floor(startDate.getTime() / 1000);
    return timestampFirstDayOfMonth + (times * 86400);
  };

  const FetchCategories = async () => {
    try {
      const res = await API({
        url: "/reports/getMetroCategories",
        method: "GET",
        responseType: "json",
      });
      if (res?.data) setCategories(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const FetchData = async () => {
    if (!category) return;
    const salesPersonIds = Array.isArray(category.sales_ids) ? category.sales_ids : [];
    try {
      const res = await API({
        url: "/reports/metrocategoryreport",
        method: "POST",
        responseType: "json",
        data: {
          team: TEAM,
          category_id: category.value,
          sales_person_ids: salesPersonIds,
          startTimestamp,
          endTimestamp,
          newReport: newReports,
        },
      });
      if (res.code === 200) {
        if (res.data && Array.isArray(res.data.currentMonth)) {
          setTeamData(res.data.currentMonth);
          setPreviousMonths(Array.isArray(res.data.previousMonths) ? res.data.previousMonths : []);
          setSalesPersons(Array.isArray(res.data.salesPersons) ? res.data.salesPersons : []);
        } else {
          setTeamData(Array.isArray(res.data) ? res.data : []);
          setPreviousMonths([]);
          setSalesPersons([]);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const calculateSpentByIds = (salesIds, index) => {
    const targetDate = getTimestamp(index);
    return teamData.reduce((sum, entry) => {
      if (salesIds.includes(entry.sales_id) && entry.date === targetDate) {
        return sum + (parseFloat(entry.spent) || 0);
      }
      return sum;
    }, 0);
  };

  const CalculateTotal = (index) => {
    const targetDate = getTimestamp(index);
    const totalSpent = teamData.reduce((sum, entry) => {
      if (entry.date === targetDate && entry.spent) return sum + entry.spent;
      return sum;
    }, 0);
    return Math.round(parseFloat(totalSpent) || 0);
  };

  function calculateSpentColumnTotal(salesIds) {
    if (!teamData.length) return 0;
    return teamData.reduce((sum, item) => {
      if (salesIds.includes(item.sales_id)) return sum + (parseFloat(item.spent) || 0);
      return sum;
    }, 0);
  }

  const CalculateSalesTeamTotal = () => {
    if (!teamData.length) return 0;
    const sum = teamData.reduce((s, item) => s + (parseFloat(item.spent) || 0), 0);
    return Math.round(parseFloat(sum) || 0);
  };

  function capitalizeFirstLetter(string) {
    return string?.charAt(0).toUpperCase() + string?.slice(1);
  }

  function getMonthName(monthNumber) {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];
    if (monthNumber >= 1 && monthNumber <= 12) return months[monthNumber - 1];
    return "";
  }

  const displayedTeamMembers = useMemo(() => {
    if (!Array.isArray(salesPersons) || !salesPersons.length) return [];
    const otherMembers = salesPersons.filter((m) => m?.status === 0);
    const normalMembers = salesPersons.filter((m) => m?.status !== 0);
    if (!otherMembers.length) return salesPersons;
    return [
      ...normalMembers,
      {
        name: "Others",
        isClubbed: true,
        sales_person_ids: otherMembers.map((m) => m.sales_person_id),
      },
    ];
  }, [salesPersons]);

  useEffect(() => {
    FetchCategories();
  }, []);

  useEffect(() => {
    setCurrentDay(getReportLastDay(year, month - 1));
  }, [year, month]);

  useEffect(() => {
    if (category) FetchData();
  }, [category, month, year, newReports]);

  return (
    <>
      <div className="d-flex gap-3 align-items-end mb-3">
        <div style={{ minWidth: 260 }}>
          <h5>Select Category</h5>
          <Select
            options={categories}
            value={category}
            onChange={(opt) => setCategory(opt)}
            placeholder="Select category"
          />
        </div>
        {category && (
          <h6 className="mb-2">Selected Category: {category.label}</h6>
        )}
      </div>

      <div className="report-image">
        {category ? (
          <div className="overflow-x-auto responsive-table">
            <table className="table table-bordered report-table" id="metro-category-report">
              <thead>
                <tr>
                  <td
                    colSpan={displayedTeamMembers.length + 2}
                    style={{
                      background: COLORS.titleBg,
                      color: COLORS.titleText,
                      textAlign: "center",
                    }}
                  >
                    <h4 className="m-0" style={{ fontWeight: "bold" }}>
                      {`${category.label}_Metro Sales - ${getMonthName(Number(month))}`}
                    </h4>
                  </td>
                </tr>
                <tr
                  style={{
                    background: COLORS.headerBg,
                    color: COLORS.headerText,
                  }}
                >
                  <th style={{ background: COLORS.headerBg, color: COLORS.headerText }}>Date</th>
                  {displayedTeamMembers?.map((item, index) => (
                    <th
                      key={item?.sales_person_id || item?.name || index}
                      style={{ background: COLORS.headerBg, color: COLORS.headerText }}
                    >
                      {item?.name}
                    </th>
                  ))}
                  <th style={{ background: COLORS.headerBg, color: COLORS.headerText }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: currentDay }, (_, index) => (
                  <tr key={index}>
                    <td>{`${index + 1}-${month}-${year}`}</td>
                    {displayedTeamMembers?.map((item, index1) => {
                      const ids = item?.isClubbed
                        ? item.sales_person_ids
                        : [item.sales_person_id];
                      const rawVal = calculateSpentByIds(ids, index);
                      return (
                        <td key={`${item?.name}-${index1}`} title={rawVal}>
                          {Math.round(parseFloat(rawVal) || 0)}
                        </td>
                      );
                    })}
                    <td title={CalculateTotal(index)}>{CalculateTotal(index)}</td>
                  </tr>
                ))}
                <tr style={{ background: COLORS.totalRowBg, fontWeight: "bold" }}>
                  <td style={{ background: COLORS.totalRowBg, textAlign: "center" }}>
                    <strong>Total</strong>
                  </td>
                  {displayedTeamMembers?.map((item, index) => {
                    const ids = item?.isClubbed
                      ? item.sales_person_ids
                      : [item.sales_person_id];
                    const totalVal = parseFloat(calculateSpentColumnTotal(ids));
                    return (
                      <td
                        key={`${item?.name}-total-${index}`}
                        title={totalVal}
                        style={{ background: COLORS.totalRowBg }}
                      >
                        {Math.round(totalVal || 0)}
                      </td>
                    );
                  })}
                  <td
                    title={CalculateSalesTeamTotal()}
                    style={{ background: COLORS.totalRowBg }}
                  >
                    {CalculateSalesTeamTotal()}
                  </td>
                </tr>
                {previousMonths.map((pm, idx) => {
                  const label =
                    pm?.year && pm?.month
                      ? getPreviousMonthLabel(pm.year, pm.month)
                      : `Previous ${idx + 1}`;
                  return (
                    <PreviousMonthRow
                      key={`pm-${idx}`}
                      label={label}
                      data={Array.isArray(pm?.data) ? pm.data : []}
                      displayedTeamMembers={displayedTeamMembers}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </>
  );
}

export default MetroCategoryReport;
